import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { 
  MessageSquare, 
  Target, 
  Calendar, 
  Zap, 
  Home as HomeIcon, 
  Heart, 
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  Moon,
  AlertCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Award,
  DollarSign,
  Ban,
  Utensils,
  Dumbbell,
  Calculator as CalculatorIcon,
  Scissors
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { mockData } from '../mock';
import WhatsAppButton from '../components/WhatsAppButton';
import ROICalculator from '../components/ROICalculator';

const iconMap = {
  MessageSquare,
  Target,
  Calendar,
  Zap,
  Home: HomeIcon,
  Heart,
  Clock,
  Moon,
  AlertCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Award,
  DollarSign,
  Ban,
  Sparkles,
  Utensils,
  Dumbbell,
  Calculator: CalculatorIcon,
  Scissors
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleScheduleDemo = () => {
    if (mockData.contact.whatsapp) {
      window.open(`https://wa.me/${mockData.contact.whatsapp}?text=${encodeURIComponent("Olá, vim pelo site da Fluxo Tech IA.")}`, '_blank');
    } else {
      alert('Em breve! Nosso WhatsApp estará disponível em breve.');
    }
  };

  return (
    <div className="home-container">
      {/* WhatsApp Float Button */}
      <WhatsAppButton />
      
      {/* Header */}
      <header className="header">
        <div className="container">
          <motion.div 
            className="header-content"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <img 
              src={mockData.company.logo} 
              alt={mockData.company.name}
              className="logo"
            />
          </motion.div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <motion.div
              initial={{ opacity: 0, y: 150 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ ...springConfig, delay: 0.1 }}
              className="hero-text"
            >
              <h1 className="hero-title">
                {mockData.company.tagline}
              </h1>
              <p className="hero-subtitle">
                {mockData.company.description}
              </p>
              <div className="hero-cta">
                <Button 
                  onClick={handleScheduleDemo}
                  className="btn-primary-large"
                >
                  {mockData.cta.buttonText}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="section-header"
          >
            <h2 className="section-title problem-title">{mockData.problem.title}</h2>
            <p className="section-subtitle problem-subtitle">
              {mockData.problem.subtitle}
            </p>
          </motion.div>

          <div className="problem-grid">
            {mockData.problem.issues.map((issue, index) => {
              const IconComponent = iconMap[issue.icon];
              return (
                <motion.div
                  key={issue.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className="problem-card">
                    <div className="problem-icon-warning">
                      <IconComponent size={28} />
                    </div>
                    <h3 className="problem-card-title">{issue.problem}</h3>
                    <p className="problem-card-cost">{issue.cost}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Speed to Lead Section */}
      <section className="speed-to-lead-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="speed-content"
          >
            <h2 className="speed-title">{mockData.speedToLead.title}</h2>
            <p className="speed-subtitle">{mockData.speedToLead.subtitle}</p>
            <p className="speed-description">{mockData.speedToLead.description}</p>
            
            <div className="speed-stats-grid">
              {mockData.speedToLead.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.15 }}
                  className={`speed-stat-card speed-${stat.color}`}
                >
                  <div className="speed-stat-time">{stat.time}</div>
                  <div className="speed-stat-impact">{stat.impact}</div>
                  <div className="speed-stat-description">{stat.description}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.5 }}
              className="speed-comparison"
            >
              <h3 className="comparison-title">{mockData.speedToLead.comparison.title}</h3>
              <ul className="comparison-list">
                {mockData.speedToLead.comparison.problems.map((problem, idx) => (
                  <li key={idx} className="comparison-item">
                    <XCircle size={20} className="comparison-icon" />
                    <span>{problem}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* Traffic Waste Section - NEW */}
      <section className="traffic-waste-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="traffic-waste-content"
          >
            <h2 className="traffic-waste-title">{mockData.trafficWaste.title}</h2>
            <p className="traffic-waste-subtitle">{mockData.trafficWaste.subtitle}</p>
            <p className="traffic-waste-description">{mockData.trafficWaste.description}</p>
            
            <div className="traffic-stats-grid">
              {mockData.trafficWaste.stats.map((stat, index) => {
                const IconComponent = iconMap[stat.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.15 }}
                    className="traffic-stat-card"
                  >
                    <IconComponent size={32} className="traffic-stat-icon" />
                    <div className="traffic-stat-value">{stat.value}</div>
                    <div className="traffic-stat-label">{stat.label}</div>
                    <div className="traffic-stat-sublabel">{stat.sublabel}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="section-header"
          >
            <h2 className="section-title">A Solução: IA de Vendas Real</h2>
            <p className="section-subtitle">
              Não é automação comum. É inteligência artificial que vende enquanto você cuida do negócio.
            </p>
          </motion.div>

          <div className="services-grid">
            {mockData.services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className="service-card">
                    {service.tag && <div className="service-tag">{service.tag}</div>}
                    <div className="service-icon">
                      <IconComponent size={32} />
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="section-header"
          >
            <h2 className="section-title">Casos Reais de Sucesso</h2>
            <p className="section-subtitle">
              Resultados comprovados em diversos segmentos
            </p>
          </motion.div>

          <div className="use-cases-grid-expanded">
            {mockData.useCases.map((useCase, index) => {
              const IconComponent = iconMap[useCase.icon];
              return (
                <motion.div
                  key={useCase.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className="use-case-card-compact">
                    <div className="use-case-header-compact">
                      <div className="use-case-icon-compact">
                        <IconComponent size={28} />
                      </div>
                      <span className="use-case-industry-compact">{useCase.industry}</span>
                    </div>
                    <h3 className="use-case-title-compact">{useCase.title}</h3>
                    <p className="use-case-description-compact">{useCase.description}</p>
                    <div className="use-case-roi-compact">
                      <TrendingUp size={16} className="roi-icon-compact" />
                      <span className="roi-text-compact">{useCase.roi}</span>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="about-content"
          >
            <div className="about-text">
              <h2 className="about-title">{mockData.about.title}</h2>
              <p className="about-description">{mockData.about.description}</p>
            </div>
            
            <div className="differentials-grid">
              {mockData.about.differentials.map((diff, index) => {
                const IconComponent = iconMap[diff.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.1 }}
                    className="differential-card"
                  >
                    <div className="differential-icon">
                      <IconComponent size={28} />
                    </div>
                    <h3 className="differential-title">{diff.title}</h3>
                    <p className="differential-description">{diff.description}</p>
                  </motion.div>
                );
              })}
            </div>
            
            <div className="stats-grid">
              {mockData.about.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="stat-card"
                >
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="cta-content"
          >
            <DollarSign className="cta-icon" size={48} />
            <h2 className="cta-title">{mockData.cta.title}</h2>
            <p className="cta-subtitle">{mockData.cta.subtitle}</p>
            <Button 
              onClick={handleScheduleDemo}
              className="btn-primary-large btn-cta-large"
            >
              {mockData.cta.buttonText}
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <p className="cta-urgency">{mockData.cta.urgency}</p>
            {mockData.cta.guarantee && (
              <p className="cta-guarantee">{mockData.cta.guarantee}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img 
                src={mockData.company.logo} 
                alt={mockData.company.name}
                className="footer-logo-img"
              />
            </div>
            <div className="footer-text">
              <p>© 2025 {mockData.company.name}. Todos os direitos reservados.</p>
              {mockData.contact.email && (
                <p className="footer-email">{mockData.contact.email}</p>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
